/**
 * Configuration for the routing algorithm.
 * DEFAULT_CONFIG values are spec-mandated and mirror the Go canonical
 * and iOS/Android implementations.
 */
export interface AlgorithmConfig {
  /** Maximum number of route variants to return. */
  readonly maxVariants: number
  /** Maximum number of transfers allowed in a route. */
  readonly maxTransfers: number
  /** Maximum total time ratio relative to the best route. */
  readonly maxTimeRatio: number
  /** Minimum stops on the same line before flagging as a loop. */
  readonly minSameLineLoopStops: number
  /** Walk transfers below this duration (sec) are considered degenerate. */
  readonly degenerateWalkBelowSec: number
  /** Average ride speed in px/sec — used by A* heuristic. */
  readonly avgRideSpeedPxPerSec: number
  /**
   * Time penalty (seconds) applied per extra transfer when comparing
   * routes for transfer-count dominance pruning. Route Y is dropped
   * when there exists a route X with strictly fewer transfers AND
   * `X.totalTime + (Y.transfers - X.transfers) * penalty <= Y.totalTime`.
   *
   * Example: at penalty = 180, a 16-min 1-transfer route dominates a
   * 22-min 3-transfer alternative (16 + 2 × 3 = 22 ≤ 22 → drop the
   * 3-transfer variant). User would never pick the longer multi-hop
   * option for the same arrival time.
   */
  readonly transferDominancePenaltySec: number
}

export const DEFAULT_CONFIG: AlgorithmConfig = {
  maxVariants: 8,
  maxTransfers: 4,
  maxTimeRatio: 1.5,
  minSameLineLoopStops: 2,
  degenerateWalkBelowSec: 30,
  avgRideSpeedPxPerSec: 0.5,
  transferDominancePenaltySec: 180,
}
